import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, Grid, makeStyles } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import Image from '../../../assets/KLP_sorterer_papirer.png';
import type { IContractToRegistrationProcessActivity } from '../../../classes/ContractToRegistrationProcessResponse';
import type { RegistrationStatuses } from '../../../services/rtkApi';
import { useExecuteTaskMutation } from '../../../services/rtkApi';
import { Tooltip, Typography, Block, Loader } from '@klp/design';
import {
    handleRequiredFormFields,
    useMapUserRolesToCandidateGroup,
} from '../../../utils';
import { FormContainerKontraktsforvaltning } from '@klp/formgenerator/index';
import { contractregistrationSelectors } from '@klp/contractregistration/contractToRegistrationSlice';
import { showToast } from '@klpkonsern/ui';

const useStyles = makeStyles(() => ({
    image: {
        '& > img': {
            width: '45%',
            minHeight: '1px', // IE11 hack
            maxWidth: '45%',
        },
    },
    buttonWrapper: {
        width: 'max-content',
        cursor: 'not-allowed',
    },
}));
interface ReviewMeetingStepProps {
    handleCompleteStep: () => void;
    activity: IContractToRegistrationProcessActivity;
    status: RegistrationStatuses.GODKJENT;
    prosessInstansId: string;
}

export default function ReviewMeetingStep({
    handleCompleteStep,
    status,
    activity,
    prosessInstansId,
}: ReviewMeetingStepProps) {
    const { t } = useTranslation();
    const anchorRef = useRef<HTMLButtonElement>(null);
    const classes = useStyles();
    const { formFieldProcessing, formFieldError } = useSelector(
        contractregistrationSelectors,
    );

    const [
        executeTask,
        { isLoading: executeTaskLoading, isError: executeTaskError },
    ] = useExecuteTaskMutation();

    const { isUserCandidateForActivity } =
        useMapUserRolesToCandidateGroup(activity);

    const formFieldIsRequired = handleRequiredFormFields(activity?.formFields);

    const isLoading = executeTaskLoading || formFieldProcessing;

    const baseIsDisabled =
        (activity?.id !== null && activity?.utfortDato !== null) ||
        activity?.id === null ||
        executeTaskLoading ||
        formFieldProcessing ||
        !isUserCandidateForActivity;

    const isDisabled =
        (activity?.id !== null && activity?.utfortDato !== null) ||
        activity?.id === null ||
        executeTaskLoading ||
        formFieldProcessing ||
        !isUserCandidateForActivity ||
        formFieldError ||
        formFieldIsRequired;

    const isDisabledWithoutFormError = baseIsDisabled;

    const handleComplete = () => {
        const taskId = activity?.id;

        if (handleCompleteStep) {
            handleCompleteStep();
        }

        if (!status || !taskId) {
            return;
        }

        executeTask({
            oppgaveId: taskId,
            registreringStatus: status,
        });
    };

    if (executeTaskError) {
        showToast({
            body: t('contractRegistration.error'),
            type: 'error',
        });
    }

    return (
        <>
            <Block className={classes.image} paddingTop="tiny">
                <img src={Image} alt="Klp sorterer papirer" />
            </Block>
            <Block paddingLeft="compact" paddingTop="tiny" paddingBottom="tiny">
                <Typography>
                    {t(
                        `checkOffContract.step.${activity?.taskDefinitionId}.descriptionPartOne`,
                    )}
                </Typography>
            </Block>
            <Block paddingTop="tiny">
                <Grid
                    container={true}
                    direction="row"
                    justifyContent="flex-start"
                >
                    <Grid item={true}>
                        <Block>
                            {activity.formFields && (
                                <FormContainerKontraktsforvaltning
                                    activity={activity}
                                    prosessInstansId={prosessInstansId}
                                    isDisabled={isDisabledWithoutFormError}
                                />
                            )}
                        </Block>
                    </Grid>
                    <Grid item={true}>
                        <Block paddingLeft="compact">
                            {isLoading ? (
                                <Loader isLoading={isLoading} />
                            ) : (
                                <Block>
                                    {isDisabled ? (
                                        <Block>
                                            <Tooltip
                                                translation="checkOffContract.tooltip"
                                                title=""
                                            >
                                                <Box
                                                    className={
                                                        classes.buttonWrapper
                                                    }
                                                >
                                                    <Button
                                                        disabled={isDisabled}
                                                        variant="contained"
                                                        color="secondary"
                                                        onClick={handleComplete}
                                                    >
                                                        {t(
                                                            `checkOffContract.step.${activity?.taskDefinitionId}.approveRegistrationButton`,
                                                        )}
                                                    </Button>
                                                </Box>
                                            </Tooltip>
                                        </Block>
                                    ) : (
                                        <>
                                            <Button
                                                disabled={isLoading}
                                                ref={anchorRef}
                                                variant="contained"
                                                color="secondary"
                                                onClick={handleComplete}
                                            >
                                                {t(
                                                    `checkOffContract.step.${activity?.taskDefinitionId}.approveRegistrationButton`,
                                                )}
                                            </Button>
                                        </>
                                    )}
                                </Block>
                            )}
                        </Block>
                    </Grid>
                </Grid>
            </Block>
        </>
    );
}
